import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { gramsabhaAPI } from '../../services/api';
import { formatMarathiNumber, toMarathiDigits } from '../../utils/marathi';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import './GramsabhaPage.css';

const MEETING_TYPE_LABELS = {
  regular: 'नियमित ग्रामसभा',
  special: 'विशेष ग्रामसभा',
  emergency: 'आपत्कालीन ग्रामसभा',
};

const STATUS_LABELS = {
  scheduled: 'नियोजित (Upcoming)',
  in_progress: 'प्रगतीत (In Progress)',
  completed: 'संपन्न (Completed)',
  cancelled: 'रद्द (Cancelled)',
};

const formatDate = (value) => {
  if (!value) return '—';
  try {
    return toMarathiDigits(
      new Date(`${value}T00:00:00`).toLocaleDateString('mr-IN', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    );
  } catch {
    return value;
  }
};

const formatTime = (value) => {
  if (!value) return '';
  const timeStr = String(value).slice(0, 5);
  return `${toMarathiDigits(timeStr)} वा.`;
};

export const getMeetingEffectiveStatus = (meeting) => {
  if (!meeting) return 'scheduled';
  if (meeting.status === 'cancelled') return 'cancelled';
  if (meeting.status === 'completed' || meeting.minutes || meeting.mom_attachment_url) return 'completed';
  if (meeting.status === 'in_progress') return 'in_progress';

  // Check if meeting date & time has already passed
  if (meeting.meeting_date) {
    const timeStr = String(meeting.meeting_time || '23:59:59').slice(0, 8);
    const meetingDateTime = new Date(`${meeting.meeting_date}T${timeStr}`);
    if (!isNaN(meetingDateTime.getTime()) && meetingDateTime < new Date()) {
      return 'completed';
    }
  }
  return meeting.status || 'scheduled';
};

export default function GramsabhaPage() {
  const { tenantSlug, meetingId } = useParams();
  const navigate = useNavigate();

  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'scheduled' | 'completed' | 'with_mom'
  const [search, setSearch] = useState('');
  const [selectedMeetingId, setSelectedMeetingId] = useState(meetingId || null);

  const loadMeetings = useCallback(async () => {
    if (!tenantSlug) return;
    setLoading(true);
    try {
      const res = await gramsabhaAPI.getAll(tenantSlug);
      setMeetings(res.data.meetings || []);
    } catch (err) {
      setError('ग्रामसभा माहिती लोड करताना त्रुटी आली.');
    } finally {
      setLoading(false);
    }
  }, [tenantSlug]);

  useEffect(() => {
    loadMeetings();
  }, [loadMeetings]);

  useEffect(() => {
    if (meetingId) {
      setSelectedMeetingId(meetingId);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      setSelectedMeetingId(null);
    }
  }, [meetingId]);

  const selectedMeeting = useMemo(() => {
    if (!selectedMeetingId) return null;
    return meetings.find(m => String(m.id) === String(selectedMeetingId)) || null;
  }, [meetings, selectedMeetingId]);

  const stats = useMemo(() => {
    const total = meetings.length;
    const upcoming = meetings.filter(m => getMeetingEffectiveStatus(m) === 'scheduled').length;
    const completed = meetings.filter(m => getMeetingEffectiveStatus(m) === 'completed').length;
    const resolutions = meetings.reduce((acc, m) => acc + (m.GramsabhaResolutions?.length || 0), 0);
    return { total, upcoming, completed, resolutions };
  }, [meetings]);

  const filteredMeetings = useMemo(() => {
    const list = meetings.filter(m => {
      const effStatus = getMeetingEffectiveStatus(m);
      if (statusFilter === 'scheduled' && effStatus !== 'scheduled') return false;
      if (statusFilter === 'completed' && effStatus !== 'completed') return false;
      if (statusFilter === 'with_mom' && !m.minutes && !m.mom_attachment_url) return false;

      if (search) {
        const q = search.toLowerCase();
        const titleMatch = (m.title || '').toLowerCase().includes(q);
        const venueMatch = (m.venue || '').toLowerCase().includes(q);
        const agendaMatch = Array.isArray(m.agenda)
          ? m.agenda.some(a => a.toLowerCase().includes(q))
          : String(m.agenda || '').toLowerCase().includes(q);
        if (!titleMatch && !venueMatch && !agendaMatch) return false;
      }
      return true;
    });

    return list.sort((a, b) => {
      const getMs = (m) => {
        const d = String(m.meeting_date || '').split('T')[0];
        const t = String(m.meeting_time || '00:00:00').slice(0, 8);
        const dt = new Date(`${d}T${t.length === 5 ? `${t}:00` : t}`);
        return isNaN(dt.getTime()) ? 0 : dt.getTime();
      };
      const aIsSched = getMeetingEffectiveStatus(a) === 'scheduled';
      const bIsSched = getMeetingEffectiveStatus(b) === 'scheduled';

      if (statusFilter === 'scheduled') {
        return getMs(a) - getMs(b);
      }
      if (statusFilter === 'completed') {
        return getMs(b) - getMs(a);
      }
      if (aIsSched && !bIsSched) return -1;
      if (!aIsSched && bIsSched) return 1;
      if (aIsSched && bIsSched) return getMs(a) - getMs(b);
      return getMs(b) - getMs(a);
    });
  }, [meetings, statusFilter, search]);

  const getAgendaItems = (agenda) => {
    if (!agenda) return [];
    if (Array.isArray(agenda)) return agenda;
    return String(agenda).split(/\r?\n|,/).map(s => s.trim()).filter(Boolean);
  };

  const handleSelectMeeting = (id) => {
    navigate(`/gp/${tenantSlug}/gramsabha/${id}`);
  };

  const handleBackToList = () => {
    navigate(`/gp/${tenantSlug}/gramsabha`);
  };

  // =========================================================================
  // VIEW 1: IN-PAGE DETAILED VIEW (WHEN A SPECIFIC MEETING IS SELECTED)
  // =========================================================================
  if (selectedMeetingId && !selectedMeeting && loading) {
    return (
      <div className="gramsabha-public-page">
        <LoadingSpinner fullPage message="ग्रामसभा तपशील लोड होत आहे..." />
      </div>
    );
  }

  if (selectedMeeting) {
    const agendaItems = getAgendaItems(selectedMeeting.agenda);
    const resolutions = selectedMeeting.GramsabhaResolutions || [];
    const hasMom = Boolean(selectedMeeting.minutes || selectedMeeting.mom_attachment_url);

    return (
      <div className="gramsabha-public-page">
        {/* Breadcrumb */}
        <div className="gramsabha-breadcrumb">
          <Link to={`/gp/${tenantSlug}`}>मुख्यपृष्ठ</Link>
          <span>›</span>
          <button type="button" onClick={handleBackToList}>ग्रामसभा</button>
          <span>›</span>
          <strong>{selectedMeeting.title}</strong>
        </div>

        <div className="gramsabha-detail-view">
          {/* Header Card */}
          <div className="gramsabha-detail-header-card">
            <button type="button" className="back-link" onClick={handleBackToList}>
              ← सर्व ग्रामसभा यादीकडे परत जा
            </button>

            <div className="gramsabha-badge-group" style={{ marginBottom: 6 }}>
              <span className="badge-pill badge-type">
                {MEETING_TYPE_LABELS[selectedMeeting.meeting_type] || selectedMeeting.meeting_type}
              </span>
              <span className={`badge-pill badge-status-${getMeetingEffectiveStatus(selectedMeeting)}`}>
                {STATUS_LABELS[getMeetingEffectiveStatus(selectedMeeting)] || getMeetingEffectiveStatus(selectedMeeting)}
              </span>
            </div>

            <h1>{selectedMeeting.title}</h1>

            <div className="header-meta-row">
              <span>📅 {formatDate(selectedMeeting.meeting_date)}</span>
              {selectedMeeting.meeting_time && <span>⏰ {formatTime(selectedMeeting.meeting_time)}</span>}
              <span>📍 {selectedMeeting.venue}</span>
            </div>
          </div>

          {/* 2-Column Detail Grid */}
          <div className="gramsabha-detail-grid">
            {/* Main Column */}
            <div className="gramsabha-detail-main">
              {/* Notice Banner if available */}
              {selectedMeeting.attachment_url && (
                <div style={{
                  background: '#eff6ff',
                  border: '1px solid #bfdbfe',
                  borderRadius: 10,
                  padding: '14px 18px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 10,
                }}>
                  <div>
                    <strong style={{ color: '#1e40af', fontSize: 13, display: 'block' }}>
                      📎 सभेची अधिकृत सूचना व अजेंडा प्रत उपलब्ध आहे
                    </strong>
                    <small style={{ color: '#64748b' }}>ग्रामपंचायतीने जारी केलेले अधिकृत परिपत्रक (PDF / Image)</small>
                  </div>
                  <a
                    href={selectedMeeting.attachment_url}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-file-download notice"
                    style={{ background: '#2563eb', color: '#fff', borderColor: '#2563eb' }}
                  >
                    पहा / डाऊनलोड करा ↗
                  </a>
                </div>
              )}

              {/* Agenda Section */}
              <div className="gramsabha-section-card">
                <h3>
                  <span>📋 सभेची अधिकृत कार्यसूची (Agenda)</span>
                  <span style={{ fontSize: 11, color: '#64748b', fontWeight: 'normal' }}>
                    {formatMarathiNumber(agendaItems.length)} विषय
                  </span>
                </h3>

                {agendaItems.length > 0 ? (
                  <ol style={{ paddingLeft: 22, margin: 0, lineHeight: 1.8, fontSize: 13, color: '#334155' }}>
                    {agendaItems.map((item, idx) => (
                      <li key={idx} style={{ marginBottom: 6 }}>
                        <strong>{item}</strong>
                      </li>
                    ))}
                  </ol>
                ) : (
                  <p style={{ color: '#94a3b8', fontSize: 12, margin: 0 }}>कार्यसूची नमूद केलेली नाही.</p>
                )}
              </div>

              {/* Minutes of Meeting (MoM / इतिवृत्त) Section */}
              <div className="gramsabha-section-card">
                <h3>
                  <span>📝 सभेचे अधिकृत इतिवृत्त (Minutes of Meeting - MoM)</span>
                  {hasMom ? (
                    <span style={{ fontSize: 11, color: '#15803d', fontWeight: 700 }}>✓ अधिकृत उपलब्ध</span>
                  ) : (
                    <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>प्रलंबित</span>
                  )}
                </h3>

                {hasMom ? (
                  <div className="gramsabha-mom-box">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
                      <span className="gramsabha-mom-badge">
                        ✓ ग्रामपंचायतीचे अधिकृत स्वाक्षरी केलेले इतिवृत्त
                      </span>
                      {selectedMeeting.mom_attachment_url && (
                        <a
                          href={selectedMeeting.mom_attachment_url}
                          target="_blank"
                          rel="noreferrer"
                          className="btn-file-download mom"
                          style={{ background: '#16a34a', color: '#fff', borderColor: '#16a34a' }}
                        >
                          📄 स्वाक्षरी केलेले इतिवृत्त डाऊनलोड ↗
                        </a>
                      )}
                    </div>

                    {selectedMeeting.minutes ? (
                      <div className="gramsabha-mom-text">
                        {selectedMeeting.minutes}
                      </div>
                    ) : (
                      <p style={{ margin: 0, fontSize: 13, color: '#475569' }}>
                        सभेचे अधिकृत इतिवृत्त स्वाक्षरी केलेल्या फाईल स्वरूपात वरील बटणावर उपलब्ध आहे.
                      </p>
                    )}
                  </div>
                ) : (
                  <div style={{ padding: 18, background: '#f8fafc', borderRadius: 8, textAlign: 'center', color: '#64748b', fontSize: 13 }}>
                    <p style={{ margin: 0 }}>
                      {selectedMeeting.status === 'scheduled'
                        ? '⏳ ही सभा नियोजित आहे. सभा संपन्न झाल्यानंतर अधिकृत इतिवृत्त (MoM) येथे प्रसिद्ध केले जाईल.'
                        : 'या सभेचे इतिवृत्त अद्याप डिजिटल अपलोड झालेले नाही.'}
                    </p>
                  </div>
                )}
              </div>

              {/* Resolutions Section */}
              <div className="gramsabha-section-card">
                <h3>
                  <span>⚖️ सभेत संमत झालेले सर्व ठराव (Resolutions)</span>
                  <span style={{ fontSize: 11, color: '#1e40af', fontWeight: 800 }}>
                    {formatMarathiNumber(resolutions.length)} ठराव
                  </span>
                </h3>

                {resolutions.length === 0 ? (
                  <p style={{ color: '#94a3b8', fontSize: 13, margin: 0, padding: 12, textAlign: 'center' }}>
                    या सभेसाठी अद्याप कोणतेही ठराव नोंदवलेले नाहीत.
                  </p>
                ) : (
                  <div>
                    {resolutions.map((r) => (
                      <div key={r.id} className="resolution-card">
                        <div className="resolution-card-header">
                          <span className="resolution-uid">{r.resolution_uid || 'ठराव'}</span>
                          <span style={{
                            fontSize: 10,
                            fontWeight: 800,
                            padding: '2px 8px',
                            borderRadius: 999,
                            background: (r.result === 'rejected' || r.status === 'rejected') ? '#fee2e2' : (r.result === 'deferred' || r.status === 'deferred') ? '#fef3c7' : '#dcfce7',
                            color: (r.result === 'rejected' || r.status === 'rejected') ? '#b91c1c' : (r.result === 'deferred' || r.status === 'deferred') ? '#b45309' : '#15803d',
                          }}>
                            {(r.result === 'rejected' || r.status === 'rejected') ? 'नामंजूर' : (r.result === 'deferred' || r.status === 'deferred') ? 'स्थगित' : 'मंजूर'}
                          </span>
                        </div>
                        <div className="resolution-title">{r.title}</div>
                        {r.description && <div className="resolution-desc">{r.description}</div>}
                        <div className="resolution-meta">
                          <span><strong>प्रस्तावक:</strong> {r.proposed_by || 'नागरिक / सदस्य'}</span>
                          {r.seconded_by && <span><strong>अनुमोदक:</strong> {r.seconded_by}</span>}
                          {r.votes_for != null && (
                            <span><strong>मतदान:</strong> समर्थन: {formatMarathiNumber(r.votes_for)}, विरोध: {formatMarathiNumber(r.votes_against || 0)}{r.votes_abstain ? `, तटस्थ: ${formatMarathiNumber(r.votes_abstain)}` : ''}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Sidebar Column */}
            <aside className="gramsabha-detail-sidebar">
              {/* Quick Facts */}
              <div className="gramsabha-section-card">
                <h3>📌 सभेचे मुख्य तपशील</h3>
                <div className="gramsabha-facts-list">
                  <div className="facts-row">
                    <span className="facts-label">सभेचा प्रकार:</span>
                    <span className="facts-val">{MEETING_TYPE_LABELS[selectedMeeting.meeting_type] || selectedMeeting.meeting_type}</span>
                  </div>
                  <div className="facts-row">
                    <span className="facts-label">तारीख:</span>
                    <span className="facts-val">{formatDate(selectedMeeting.meeting_date)}</span>
                  </div>
                  {selectedMeeting.meeting_time && (
                    <div className="facts-row">
                      <span className="facts-label">वेळ:</span>
                      <span className="facts-val">{formatTime(selectedMeeting.meeting_time)}</span>
                    </div>
                  )}
                  <div className="facts-row">
                    <span className="facts-label">स्थळ:</span>
                    <span className="facts-val">{selectedMeeting.venue}</span>
                  </div>
                  <div className="facts-row">
                    <span className="facts-label">स्थिती:</span>
                    <span className="facts-val">{STATUS_LABELS[getMeetingEffectiveStatus(selectedMeeting)] || getMeetingEffectiveStatus(selectedMeeting)}</span>
                  </div>
                  <div className="facts-row">
                    <span className="facts-label">मंजूर ठराव:</span>
                    <span className="facts-val">{formatMarathiNumber(resolutions.length)} ठराव</span>
                  </div>
                </div>
              </div>

              {/* Downloads Box */}
              <div className="gramsabha-section-card">
                <h3>📁 अधिकृत कागदपत्रे</h3>
                <div style={{ display: 'grid', gap: 10 }}>
                  {selectedMeeting.attachment_url ? (
                    <a
                      href={selectedMeeting.attachment_url}
                      target="_blank"
                      rel="noreferrer"
                      className="btn-file-download notice"
                      style={{ padding: '10px 14px', fontSize: 12, justifyContent: 'center' }}
                    >
                      📎 सभेची नोटीस प्रत डाऊनलोड
                    </a>
                  ) : (
                    <div style={{ fontSize: 11, color: '#94a3b8' }}>नोटीस प्रत संलग्न नाही.</div>
                  )}

                  {selectedMeeting.mom_attachment_url ? (
                    <a
                      href={selectedMeeting.mom_attachment_url}
                      target="_blank"
                      rel="noreferrer"
                      className="btn-file-download mom"
                      style={{ padding: '10px 14px', fontSize: 12, justifyContent: 'center' }}
                    >
                      📄 इतिवृत्त प्रत (MoM) डाऊनलोड
                    </a>
                  ) : (
                    <div style={{ fontSize: 11, color: '#94a3b8' }}>इतिवृत्त प्रत संलग्न नाही.</div>
                  )}
                </div>
              </div>

              {/* Statutory Citizen Guidance */}
              <div className="gramsabha-help-card">
                <span>🏛️</span>
                <h2>ग्रामसभेत आपला हक्क</h2>
                <p>
                  महाराष्ट्र ग्रामपंचायत अधिनियमानुसार प्रत्येक प्रौढ नागरिकाला ग्रामसभेत उपस्थित राहून चर्चा करण्याचा, प्रश्न विचारण्याचा आणि ठरावावर मतदान करण्याचा कायदेशीर अधिकार आहे.
                </p>
                <Link to={`/gp/${tenantSlug}/contact`}>ग्रामपंचायत संपर्क</Link>
              </div>
            </aside>
          </div>
        </div>
      </div>
    );
  }

  // =========================================================================
  // VIEW 2: THEME-ALIGNED LIST VIEW
  // =========================================================================
  return (
    <div className="gramsabha-public-page">
      {/* Breadcrumb */}
      <div className="gramsabha-breadcrumb">
        <Link to={`/gp/${tenantSlug}`}>मुख्यपृष्ठ</Link>
        <span>›</span>
        <strong>ग्रामसभा</strong>
        <span>›</span>
        <strong>ग्रामसभांची यादी</strong>
      </div>

      <div className="gramsabha-layout">
        {/* Left Rail */}
        <aside className="gramsabha-left-rail">
          <section className="gramsabha-side-card">
            <h2>ग्रामसभा वर्गीकरण</h2>
            <div className="gramsabha-side-filters">
              <button
                className={statusFilter === 'all' ? 'active' : ''}
                onClick={() => setStatusFilter('all')}
              >
                सर्व ग्रामसभा <span>{formatMarathiNumber(meetings.length)}</span>
              </button>
              <button
                className={statusFilter === 'scheduled' ? 'active' : ''}
                onClick={() => setStatusFilter('scheduled')}
              >
                🔔 आगामी ग्रामसभा <span>{formatMarathiNumber(stats.upcoming)}</span>
              </button>
              <button
                className={statusFilter === 'completed' ? 'active' : ''}
                onClick={() => setStatusFilter('completed')}
              >
                ✓ संपन्न ग्रामसभा <span>{formatMarathiNumber(stats.completed)}</span>
              </button>
              <button
                className={statusFilter === 'with_mom' ? 'active' : ''}
                onClick={() => setStatusFilter('with_mom')}
              >
                📝 इतिवृत्त उपलब्ध <span>{formatMarathiNumber(meetings.filter(m => m.minutes || m.mom_attachment_url).length)}</span>
              </button>
            </div>
          </section>

          <section className="gramsabha-help-card">
            <span>☊</span>
            <h2>नागरिक सहभाग</h2>
            <p>ग्रामसभेच्या विषयांवर अधिक माहिती हवी असल्यास ग्रामपंचायत कार्यालयाशी संपर्क साधावा.</p>
            <Link to={`/gp/${tenantSlug}/contact`}>संपर्क साधा</Link>
          </section>
        </aside>

        {/* Main Rail */}
        <main className="gramsabha-main">
          {/* Theme-Matched Hero Card */}
          <section className="gramsabha-hero-card">
            <div>
              <span>ग्रामपंचायत लोकशाही व्यासपीठ</span>
              <h1>ग्रामसभा व निर्णय प्रक्रिया</h1>
              <p>ग्रामपंचायतीच्या आगामी व संपन्न झालेल्या सर्व ग्रामसभा, अधिकृत अजेंडा, मंजूर झालेले ठराव आणि स्वाक्षरी केलेले इतिवृत्त (MoM).</p>
            </div>
          </section>

          {/* Stats Grid */}
          <section className="gramsabha-stats-grid">
            <div className="gramsabha-stat">
              <span>🏛️</span>
              <p>
                <strong>{formatMarathiNumber(stats.total)}</strong>
                <small>एकूण ग्रामसभा</small>
                <em>सर्व नोंदी</em>
              </p>
            </div>
            <div className="gramsabha-stat orange">
              <span>🔔</span>
              <p>
                <strong>{formatMarathiNumber(stats.upcoming)}</strong>
                <small>आगामी सभा</small>
                <em>नियोजित तारखा</em>
              </p>
            </div>
            <div className="gramsabha-stat green">
              <span>✓</span>
              <p>
                <strong>{formatMarathiNumber(stats.completed)}</strong>
                <small>संपन्न सभा</small>
                <em>इतिवृत्तासह</em>
              </p>
            </div>
            <div className="gramsabha-stat purple">
              <span>⚖️</span>
              <p>
                <strong>{formatMarathiNumber(stats.resolutions)}</strong>
                <small>मंजूर ठराव</small>
                <em>लोकहित निर्णय</em>
              </p>
            </div>
          </section>

          {/* Toolbar */}
          <section className="gramsabha-toolbar">
            <input
              type="text"
              placeholder="शीर्षक, स्थळ किंवा अजेंडा विषयाने शोधा..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
            >
              <option value="all">सर्व स्थिती</option>
              <option value="scheduled">केवळ आगामी</option>
              <option value="completed">केवळ संपन्न</option>
              <option value="with_mom">केवळ इतिवृत्त उपलब्ध</option>
            </select>
          </section>

          {error && <div className="alert alert-error">{error}</div>}

          {/* Meeting Cards */}
          {loading ? (
            <LoadingSpinner message="ग्रामसभा माहिती लोड होत आहे..." />
          ) : filteredMeetings.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '50px 20px', background: '#fff', borderRadius: 10, border: '1px solid var(--gp-line, #dbe4ee)' }}>
              <p style={{ fontSize: 14, color: '#64748b', margin: 0 }}>
                {search ? 'शोध परिणामांनुसार कोणतीही नोंद आढळली नाही.' : 'ग्रामसभा नोंदी उपलब्ध नाहीत.'}
              </p>
            </div>
          ) : (
            <div className="gramsabha-cards-list">
              {filteredMeetings.map((m) => {
                const agendaItems = getAgendaItems(m.agenda);
                const hasMom = Boolean(m.minutes || m.mom_attachment_url);
                const resCount = m.GramsabhaResolutions?.length || 0;

                return (
                  <article key={m.id} className="gramsabha-item-card">
                    <div className="gramsabha-item-header">
                      <div>
                        <h2 className="gramsabha-item-title">{m.title}</h2>
                        <div className="gramsabha-item-meta">
                          <div className="gramsabha-item-meta-item">
                            <span>📅</span>
                            <span>{formatDate(m.meeting_date)}</span>
                            {m.meeting_time && <span>({formatTime(m.meeting_time)})</span>}
                          </div>
                          <div className="gramsabha-item-meta-item">
                            <span>📍</span>
                            <span>{m.venue}</span>
                          </div>
                        </div>
                      </div>

                      <div className="gramsabha-badge-group">
                        <span className="badge-pill badge-type">
                          {MEETING_TYPE_LABELS[m.meeting_type] || m.meeting_type}
                        </span>
                        <span className={`badge-pill badge-status-${getMeetingEffectiveStatus(m)}`}>
                          {STATUS_LABELS[getMeetingEffectiveStatus(m)] || getMeetingEffectiveStatus(m)}
                        </span>
                      </div>
                    </div>

                    {agendaItems.length > 0 && (
                      <div className="gramsabha-agenda-snippet">
                        <strong>📋 कार्यसूची:</strong>{' '}
                        {agendaItems.slice(0, 2).join(' • ')}
                        {agendaItems.length > 2 && ` (आणखी ${formatMarathiNumber(agendaItems.length - 2)} विषय...)`}
                      </div>
                    )}

                    <div className="gramsabha-item-actions">
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                        {m.attachment_url && (
                          <a
                            href={m.attachment_url}
                            target="_blank"
                            rel="noreferrer"
                            className="btn-file-download notice"
                          >
                            📎 नोटीस प्रत
                          </a>
                        )}
                        {hasMom && (
                          <span style={{ fontSize: 11, color: '#15803d', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            ✓ इतिवृत्त उपलब्ध
                          </span>
                        )}
                        {resCount > 0 && (
                          <span style={{ fontSize: 11, color: '#64748b' }}>
                            ⚖️ {formatMarathiNumber(resCount)} ठराव
                          </span>
                        )}
                      </div>

                      <button
                        type="button"
                        className="btn-view-detail"
                        onClick={() => handleSelectMeeting(m.id)}
                      >
                        सविस्तर तपशील पहा →
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
